import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Loader2, GraduationCap } from "lucide-react";
import { AdminRoute } from "./components/AdminRoute";
import { AdminSetup } from "./components/AdminSetup";

// Lazy load all page components to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Landing = lazy(() => import("./pages/Landing"));
const AddClass = lazy(() => import("./pages/AddClass"));
const Setup = lazy(() => import("./pages/Setup"));
const TestConnection = lazy(() => import("./pages/TestConnection"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const queryClient = new QueryClient();

// Enhanced loading fallback component with immediate content paint
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
    {/* Header that matches the actual site */}
    <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img 
              src="/classtrack-logo-icon.png" 
              alt="ClassTrack Logo" 
              className="h-10 w-10"
            />
            <span className="text-xl font-bold text-gray-900">
              ClassTrack
            </span>
          </div>
        </div>
      </div>
    </header>
    
    {/* Loading content */}
    <div className="flex items-center justify-center pt-20">
      <div className="text-center space-y-4 max-w-md mx-auto px-4">
        <div className="p-3 bg-white rounded-xl shadow-sm border">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Loading ClassTrack</h2>
          <p className="text-gray-600 text-sm">Setting up your academic management tools...</p>
        </div>
      </div>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/app" element={<Index />} />
            <Route path="/add-class" element={<AddClass />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/test-connection" element={<TestConnection />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/admin-setup" element={<AdminSetup />} />
            
            {/* Admin Routes - Protected */}
            <Route 
              path="/admin/*" 
              element={
                <AdminRoute>
                  <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<div className="p-8 text-center">User Management - Coming Soon</div>} />
                  </Routes>
                </AdminRoute>
              } 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
